import api from './api';

/**
 * Cartera del asesor cd-2b (docs/72): las EMPRESAS que atiende.
 *
 * Distinta de `referralService`, que lista REFERIDOS —usuarios, con su estado de comisión—.
 * Aquí la unidad es el negocio (nombre, giro, tenant, edición, MRR) y el conjunto es
 * ASIGNADAS ∪ VENDIDAS: filtrar solo por lo vendido dejaría fuera todo lo que se registró
 * solo o llegó por traspaso.
 */
const carteraService = {
  async getCartera(filters = {}, options = {}) {
    const params = {
      ...filters,
      page: options.page || 1,
      limit: options.limit || 20,
    };
    const response = await api.get('/partners/cartera', { params });
    return response.data;
  },

  /** Etapa de cobro de la empresa y qué se le puede enviar al dueño (`verificar` consulta a Wompi). */
  async cobroEmpresa(subId, { verificar = false } = {}) {
    const response = await api.get(`/partners/clientes/${subId}/cobro`, { params: verificar ? { verificar: 1 } : {} });
    return response.data;
  },

  /** Envía al dueño el cobro que toque: para contratar, primer pago o renovación del periodo. */
  async enviarCobroEmpresa(subId) {
    const response = await api.post(`/partners/clientes/${subId}/cobro/enviar`);
    return response.data;
  },

  /** Ficha de UNA empresa: paquete + alcance real del tenant, addendas, solicitudes (docs/71 P9). */
  async getFicha(subId) {
    const response = await api.get(`/partners/clientes/${subId}`);
    return response.data;
  },

  /** Fija la lista COMPLETA de áreas ocultas por el dueño (no un delta). No cambia plan/precio. */
  async ocultarAreas(subId, areasOcultas) {
    const response = await api.post(`/partners/clientes/${subId}/ocultar`, { areasOcultas });
    return response.data;
  },

  /** Panel de impacto de un cambio de paquete (precio + comisión antes/después). No persiste. */
  async impactoAddenda(subId, cambio) {
    const response = await api.post(`/partners/clientes/${subId}/addenda/impacto`, cambio);
    return response.data;
  },

  /**
   * Empresa EN PRUEBA: cambia el paquete sin cobrar. Edita la cotización de origen (es lo que el
   * cliente pagará para pasar a Activa) y lo aplica a la demo al momento.
   */
  async cambiarPaqueteEnPrueba(subId, cambio) {
    const response = await api.post(`/partners/clientes/${subId}/paquete-prueba`, cambio);
    return response.data;
  },

  /** Crea la addenda (borrador) congelando el antes y el diff. */
  async crearAddenda(subId, cambio) {
    const response = await api.post(`/partners/clientes/${subId}/addenda`, cambio);
    return response.data;
  },

  /**
   * Aplica un recorte (baja al próximo ciclo). Ampliación: exige pagoConciliado o link de pago.
   *
   * 🔴 `aceptacionConciliada` se destructuraba fuera y NO viajaba en el cuerpo: el botón «Ya lo
   * aceptó (conciliado)» mandaba `{pagoConciliado:false}` y el API contestaba 409
   * REQUIERE_APROBACION — el único camino para aplicar un recorte que el dueño aceptó por
   * teléfono estaba cerrado, y el 409 parecía un fallo del flujo.
   */
  async aplicarAddenda(addendaId, { pagoConciliado = false, aceptacionConciliada = false } = {}) {
    const response = await api.post(`/partners/addendas/${addendaId}/aplicar`, { pagoConciliado, aceptacionConciliada });
    return response.data;
  },

  /** Link de pago del prorrateo de una ampliación. */
  /** Envía la cotización de upgrade al dueño por correo (con link de aceptación). */
  async enviarAddendaAlDueno(addendaId) {
    const response = await api.post(`/partners/addendas/${addendaId}/enviar-dueno`)
    return response.data
  },

  async linkAddenda(addendaId) {
    const response = await api.post(`/partners/addendas/${addendaId}/link`);
    return response.data;
  },

  /** Cuenta del dueño: correo, si ya entró y si el asesor puede corregirlo. */
  async cuentaDueno(subId) {
    const response = await api.get(`/partners/clientes/${subId}/cuenta-dueno`);
    return response.data;
  },

  /** Corrige el correo del dueño (solo si nunca entró) y le envía el acceso al correo nuevo. */
  async corregirCorreoDueno(subId, nuevoEmail) {
    const response = await api.post(`/partners/clientes/${subId}/cuenta-dueno/correo`, { nuevoEmail });
    return response.data;
  },

  /** Envía al dueño el enlace para crear su contraseña. */
  async enviarAccesoDueno(subId) {
    const response = await api.post(`/partners/clientes/${subId}/cuenta-dueno/enviar-acceso`);
    return response.data;
  },

  /** Pregunta a la pasarela si el cliente ya pagó el link de la addenda (y si pagó, la aplica). */
  async verificarPagoAddenda(addendaId) {
    const response = await api.post(`/partners/addendas/${addendaId}/verificar-pago`);
    return response.data;
  },

  /** Descarta una addenda no aplicada. */
  async descartarAddenda(addendaId) {
    const response = await api.delete(`/partners/addendas/${addendaId}`);
    return response.data;
  },

  /* ── Gestión de la empresa ──────────────────────────────────────────────────
   * Van por SUSCRIPCIÓN, no por cotización. Antes estas mismas acciones solo
   * existían en el editor de la cotización, así que una empresa ya vendida —o
   * una que llegó sin cotización— no se podía trabajar desde su ficha.
   */

  /** Con qué entra el DUEÑO: código de empresa, PIN y acceso de escritorio. */
  async accesoEmpresa(subId) {
    const response = await api.get(`/partners/clientes/${subId}/acceso`);
    return response.data;
  },

  /**
   * EMITE el código de acceso (acción explícita: el GET de arriba solo lee). En una empresa ya en
   * producción el api exige `confirmar:true`.
   */
  async emitirAccesoEmpresa(subId, { confirmar = false } = {}) {
    const response = await api.post(`/partners/clientes/${subId}/acceso`, { confirmar });
    return response.data;
  },

  /** Bandeja de facturas: la dirección a la que sus proveedores le mandan los DTE. */
  async bandejaEmpresa(subId) {
    const response = await api.get(`/partners/clientes/${subId}/bandeja`);
    return response.data;
  },

  /**
   * Da bandeja de facturas a la empresa. Una empresa de MUESTRA no la recibe sola: para esas hay
   * que pasar `forzar` con un `motivo`, que queda grabado en la fila del buzón.
   */
  async asignarBandejaEmpresa(subId, { forzar = false, motivo = '' } = {}) {
    const response = await api.post(`/partners/clientes/${subId}/bandeja`, { forzar, motivo });
    return response.data;
  },

  /** Quién puede entrar y con qué PIN, puesto por puesto. */
  async credencialesEmpresa(subId) {
    const response = await api.get(`/partners/clientes/${subId}/credenciales`);
    return response.data;
  },

  /** Bitácora de cargas de datos de prueba (el semáforo de entrega). */
  async cargasEmpresa(subId) {
    const response = await api.get(`/partners/clientes/${subId}/datos-prueba`);
    return response.data;
  },

  /** Siembra una carga reversible. Solo empresas DEMO (el servidor devuelve 409 si no). */
  async sembrarEnEmpresa(subId, { giro, alcance } = {}) {
    const response = await api.post(`/partners/clientes/${subId}/datos-prueba`, { giro, alcance });
    return response.data;
  },

  /** Deshace una carga; con `previsualizar` solo cuenta qué se iría. */
  async revertirCargaEmpresa(subId, cargaId, { previsualizar = false } = {}) {
    const response = await api.delete(
      `/partners/clientes/${subId}/datos-prueba/${cargaId}${previsualizar ? '?previsualizar=1' : ''}`,
    );
    return response.data;
  },

  /** Deja la empresa de práctica sin operación, para volver a sembrarla limpia. */
  async vaciarEmpresa(subId, { previsualizar = false } = {}) {
    const response = await api.post(`/partners/clientes/${subId}/datos-prueba/vaciar`, { previsualizar });
    return response.data;
  },

  /**
   * TRASLADO DE ENTORNO — las empresas de práctica que hay para traerse a ésta.
   *
   * Existe para que el asesor ELIJA en vez de teclear un identificador como
   * `el-descanso-s-a-de-c-v-f6fa72`: una letra de más traería la empresa de otro cliente
   * encima de ésta, y el traslado reemplaza tablas.
   */
  async origenesTraslado(subId) {
    const response = await api.get(`/partners/clientes/${subId}/traslado/origenes`)
    return response.data
  },

  /** Qué viajaría y —sobre todo— qué se quedaría atrás. Solo lee: no toca ninguna de las dos empresas. */
  async previsualizarTraslado(subId, { host, tenant }) {
    const response = await api.get(`/partners/clientes/${subId}/traslado`, { params: { host, tenant } })
    return response.data
  },

  /** Trae la empresa configurada y la escribe aquí. 409 si esta empresa ya opera. */
  async trasladarEmpresa(subId, { host, tenant }) {
    const response = await api.post(`/partners/clientes/${subId}/traslado`, { host, tenant })
    return response.data
  },

  /** Qué se perdería al borrar la empresa (addendas, comisiones, cotizaciones) y por qué NO se podría, si aplica. */
  async previsualizarEliminarEmpresa(subId) {
    const response = await api.get(`/partners/clientes/${subId}/eliminacion`)
    return response.data
  },

  /** Borra la empresa ENTERA (tenant + cascada Mongo). Solo mientras siga en modo demo. */
  async eliminarEmpresa(subId, confirmacionNombre) {
    const response = await api.delete(`/partners/clientes/${subId}`, { data: { confirmacionNombre } })
    return response.data
  },

  // ── Reasignar el catálogo de cuentas (el contador que llega con historia) ────
  // A diferencia de los datos de prueba, esto SÍ tiene sentido en una empresa real: es
  // justo para ella. Lo que el dueño puede hacer desde su escritorio, el asesor lo hace
  // por él — con el mismo motor, las mismas invariantes y el mismo delta log.

  /** Diagnóstico del catálogo + historial de reasignaciones, en una llamada. */
  async diagnosticoRemap(subId) {
    const response = await api.get(`/partners/clientes/${subId}/remap-cuentas`);
    return response.data;
  },

  /** El mapeo sugerido. Lo calcula el tenant con el mismo motor que el escritorio. */
  async proponerRemap(subId, { codigosViejos, codigosNuevos } = {}) {
    const response = await api.post(`/partners/clientes/${subId}/remap-cuentas/proponer`, {
      codigosViejos,
      codigosNuevos,
    });
    return response.data;
  },

  /**
   * Previsualiza el mapeo, o lo aplica con `aplicar: true`. Es la MISMA ruta a propósito:
   * la vista previa no puede mentir porque no es otro código.
   */
  async remapCuentas(subId, remap, { aplicar = false } = {}) {
    const response = await api.post(
      `/partners/clientes/${subId}/remap-cuentas${aplicar ? '?aplicar=1' : ''}`,
      remap,
    );
    return response.data;
  },

  /** Deshace una reasignación; con `previsualizar` sólo dice si se puede. */
  async deshacerRemap(subId, remapId, { previsualizar = false } = {}) {
    const response = await api.delete(
      `/partners/clientes/${subId}/remap-cuentas/${remapId}${previsualizar ? '?previsualizar=1' : ''}`,
    );
    return response.data;
  },
};

export default carteraService;
